(() => {
  'use strict';
  const root = document.querySelector('[data-pk-deity-catalog]');
  if (!root) return;
  const ui = JSON.parse(root.querySelector('[data-catalog-labels]').textContent);
  const nav = root.querySelector('[data-catalog-nav]');
  const content = root.querySelector('[data-catalog-content]');
  const status = root.querySelector('[data-catalog-status]');
  const element = (tag, text, cls) => {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    if (cls) node.className = cls;
    return node;
  };
  const priceCache = new Map(), priceQueue = [];
  let priceRequests = 0;
  const pumpPrices = () => {
    while (priceRequests < 4 && priceQueue.length) {
      priceRequests++;
      priceQueue.shift()().finally(() => { priceRequests--; pumpPrices(); });
    }
  };
  const liveProduct = item => {
    if (!priceCache.has(item.handle)) {
      priceCache.set(item.handle, new Promise((resolve, reject) => {
        priceQueue.push(async () => {
          try {
            const path = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(item.handle)}.js`;
            const response = await fetch(path, {headers:{Accept:'application/json'}});
            if (!response.ok) throw new Error('Price unavailable');
            const product = await response.json();
            if (String(product.id) !== String(item.id).split('/').pop() || product.handle !== item.handle) throw new Error('Product mismatch');
            resolve(product);
          } catch (error) { priceCache.delete(item.handle); reject(error); }
        });
        pumpPrices();
      }));
    }
    return priceCache.get(item.handle);
  };
  const loadPrice = (price, item) => {
    liveProduct(item).then(product => {
      if (!price.isConnected) return;
      const cents = Number(product.price_min ?? product.price);
      if (!Number.isFinite(cents)) throw new Error('Invalid price');
      const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:window.Shopify?.currency?.active || 'USD'}).format(amount/100);
      if (window.PKOpeningSale) window.PKOpeningSale.renderPrice(price, product, cents, money, product.price_varies ? 'From ' : '');
      else price.textContent = money(cents);
      price.dataset.priceState = 'ready';
    }).catch(() => { if (price.isConnected) { price.textContent = 'View current price'; price.dataset.priceState = 'error'; } });
  };
  const priceObserver = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
    entries.filter(entry => entry.isIntersecting).forEach(entry => {
      const price = entry.target; priceObserver.unobserve(price);
      loadPrice(price, price.pkCatalogItem);
    });
  }, {rootMargin:'400px 0px'});
  const card = (item, mode) => {
    const article = element('article', null, 'pk-dc-card');
    article.dataset.productId = item.id;
    article.dataset.association = item.association_type;
    article.dataset.identity = item.depicted_identity_slug || '';
    const link = element('a', null, 'pk-dc-product');
    link.href = item.url;
    const image = element('img');
    image.src = item.image;
    image.alt = item.title;
    image.loading = 'lazy';
    image.width = 480;
    image.height = 480;
    link.append(image, element('h4', item.title));
    const price = element('p', 'Loading price…', 'pk-dc-price');
    price.dataset.priceState = 'loading'; price.pkCatalogItem = item;
    link.append(price);
    if (priceObserver) priceObserver.observe(price);
    else loadPrice(price, item);
    article.append(link, element('p', ui[mode], 'pk-dc-badge'));
    if (mode === 'editorial') {
      const details = element('details');
      details.append(element('summary', ui.reason), element('p', item.rationale));
      article.append(details);
    }
    return article;
  };
  const section = (heading, description, items, mode) => {
    const box = element('section', null, 'pk-dc-section');
    box.append(element('h3', `${heading} (${items.length})`), element('p', description));
    if (!items.length) box.append(element('p', ui.empty));
    const grid = element('div', null, 'pk-dc-grid');
    for (const item of items) grid.append(card(item, mode || (item.association_type === 'iconographic_confirmed' ? 'depicted' : 'editorial')));
    box.append(grid);
    return box;
  };
  Promise.all([root.dataset.referenceProducts || root.dataset.products, root.dataset.stories, root.dataset.products].map(url => fetch(url).then(response => { if (!response.ok) throw new Error('catalog'); return response.json(); })))
    .then(async ([data, stories, selection]) => {
      const liveIds = await window.PKOriginalResults.refreshCatalog();
      const isListed = item => liveIds.has(String(item.id).split('/').pop());
      for (const group of [...Object.values(data.groups), ...Object.values(data.other_figures || {})]) group.items = group.items.filter(isListed);
      data.pending = data.pending.filter(isListed);
      const entries = Object.entries(data.groups);
      const valid = new Set([...entries.map(([slug]) => slug), 'other-figures', 'under-review']);
      const selected = () => valid.has(location.hash.slice(1)) ? location.hash.slice(1) : entries[0][0];
      const navigate = (slug, label) => {
        const link = element('a', label);
        link.href = `#${slug}`;
        link.dataset.catalogGroup = slug;
        nav.append(link);
      };
      for (const [slug, group] of entries) {
        navigate(slug, group.name);
        const image = element('img'); image.src = new URL(stories[slug].asset, new URL(root.dataset.stories, location.href)).href; image.alt = ''; image.loading = 'lazy'; image.width = 60; image.height = 72; nav.lastElementChild.prepend(image);
      }
      navigate('other-figures', ui.other);
      navigate('under-review', ui.pending);
      const render = () => {
        const slug = selected();
        if (priceObserver) content.querySelectorAll('.pk-dc-price').forEach(price => priceObserver.unobserve(price));
        content.replaceChildren();
        root.dataset.activeGroup = slug;
        for (const a of nav.querySelectorAll('a')) {
          if (a.dataset.catalogGroup === slug) a.setAttribute('aria-current', 'page');
          else a.removeAttribute('aria-current');
        }
        const name = slug === 'other-figures' ? ui.other : slug === 'under-review' ? ui.pending : data.groups[slug].name;
        content.append(element('h2', name));
        if (slug === 'other-figures') {
          content.append(element('p', ui.otherNote));
          for (const group of Object.values(data.other_figures)) {
            content.append(section(group.name, ui.otherNote, group.items.filter(item => item.available), 'other'));
          }
        } else if (slug === 'under-review') {
          content.append(element('p', `${data.pending.length} — ${ui.pendingNote}`));
          const link = element('a', ui.types);
          link.href = '/collections/all';
          content.append(link);
        } else {
          const items = window.PKOriginalResults.getJewelryItems(selection, slug, true);
          const text = stories[slug];
          const intro = element('section', null, 'pk-dc-introduction');
          const figure = element('figure'); const image = element('img');
          image.src = new URL(text.asset, new URL(root.dataset.stories, location.href)).href; image.alt = name + ' — cultural artwork'; image.width = 900; image.height = 1200;
          figure.append(image);
          const caption = element('figcaption', text.credit);
          if (text.source_url) { const source = element('a', 'View full artwork & source'); source.href = text.source_url; source.target = '_blank'; source.rel = 'noopener'; caption.append(element('br'), source); }
          figure.append(caption);
          const prose = element('div'); prose.append(element('p', 'THE FIGURE & THE IMAGE', 'pk-dc-eyebrow'), element('h3', 'Who is ' + name + '?'), element('p', text.intro), element('h3', 'A moment of reflection'), element('p', text.reflection));
          intro.append(figure, prose); content.append(intro);
          const jewelry = element('section', null, 'pk-dc-section');
          jewelry.append(element('h3', `${ui.allHeading} (${items.length})`), element('p', ui.allNote));
          const selectionContainer = element('div', null, 'pk-dc-selection');
          selectionContainer.addEventListener('pk:selection-count', event => { jewelry.querySelector('h3').textContent = `${ui.allHeading} (${event.detail})`; });
          jewelry.append(selectionContainer); content.append(jewelry);
          window.PKOriginalResults.renderJewelry(root, slug, selectionContainer, () => root.dataset.activeGroup === slug && selectionContainer.isConnected);
          const story = element('a', ui.culture, 'pk-dc-story');
          story.href = `/?view=pathways-deities#figure-${slug}`;
          content.append(story);
        }
        status.textContent = `${ui.selected}: ${name}`;
      };
      window.addEventListener('hashchange', () => {
        render();
        content.scrollIntoView({ block: 'start' });
      });
      render();
    }).catch(() => { status.textContent = ui.error; });
})();

